import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { imageUploadService } from '../../../utils/imageUploadService';
import { sellerService } from '../../../services/sellerService';
import { supabase } from '../../../lib/supabase'; // Import Supabase client
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Textarea from '../../../components/ui/Textarea';
import Switch from '../../../components/ui/Switch'; // Import the new Switch component
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const MyStoreTab = ({ setActiveTab }) => {
    const { user } = useAuth();
    const [sellerAccount, setSellerAccount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        storefrontBannerUrl: '',
        storefrontCompanyMessage: '',
        storefrontCategories: [],
        storefrontActive: false,
        hiddenCategories: [],
    });
    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreviewUrl, setBannerPreviewUrl] = useState('');
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [newCategory, setNewCategory] = useState(''); // State for new category input
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [visitsData, setVisitsData] = useState([]);
    const [loadingVisits, setLoadingVisits] = useState(false);
    const [offsetDays, setOffsetDays] = useState(0);

    const loadTrafficStats = async (sellerId, offset = 0) => {
        try {
            setLoadingVisits(true);
            
            // Symmetrical RPC compatibility wrapper:
            // If offset is 0, call the single-parameter RPC first to remain 100% backward compatible
            // with environments where the 2-parameter SQL migration has not been applied yet!
            let visits = null;
            let visitsError = null;
            
            if (offset === 0) {
                const res = await supabase.rpc('get_seller_daily_visits', { p_seller_id: sellerId });
                visits = res.data;
                visitsError = res.error;
            }
            
            // If it failed or we have a non-zero offset, call with the 2-parameter signature
            if (offset > 0 || (visitsError && (visitsError.code === 'PGRST202' || visitsError.message?.includes('schema cache')))) {
                const res = await supabase.rpc('get_seller_daily_visits', { 
                    p_seller_id: sellerId,
                    p_offset_days: offset 
                });
                visits = res.data;
                visitsError = res.error;
            }

            if (visitsError) {
                // If it is a 404/schema cache error, guide the developer silently
                if (visitsError.code === 'PGRST202' || visitsError.message?.includes('schema cache')) {
                    console.warn('⚠️ [Analytics Hint] get_seller_daily_visits RPC is not fully updated in Supabase. Please copy and execute the SQL migration script from: supabase/migrations/20260913000001_update_get_seller_daily_visits_rpc_offset.sql');
                } else {
                    throw visitsError;
                }
            }

            const formattedVisits = visits?.map(item => {
                const dateObj = new Date(item.visit_date);
                const label = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
                return {
                    date: label,
                    visits: Number(item.visit_count) || 0
                };
            }) || [];
            setVisitsData(formattedVisits);
        } catch (analyticsErr) {
            // Symmetrically catch and silence any analytic loading errors to guarantee zero user friction!
            console.warn('Silent fallback for site visits tracker:', analyticsErr.message || analyticsErr);
            setVisitsData([]);
        } finally {
            setLoadingVisits(false);
        }
    };

    useEffect(() => {
        if (sellerAccount?.id) {
            loadTrafficStats(sellerAccount.id, offsetDays);
        }
    }, [offsetDays, sellerAccount?.id]);

    useEffect(() => {
        const fetchSellerAccount = async () => {
            if (!user?.id) {
                setError('User not authenticated.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const data = await sellerService.getSellerAccountForUser(user.id);
                setSellerAccount(data);
                if (data) {
                    setFormData({
                        storefrontBannerUrl: data.storefrontBannerUrl || '',
                        storefrontCompanyMessage: data.storefrontCompanyMessage || '',
                        storefrontCategories: data.storefrontCategories || [],
                        storefrontActive: data.storefrontActive || false,
                        hiddenCategories: data.hiddenCategories || data.hidden_categories || [],
                    });
                    if (data.storefrontBannerUrl) {
                        setBannerPreviewUrl(data.storefrontBannerUrl);
                    }

                    // Initial Symmetrical Analytics Fetch
                    await loadTrafficStats(data.id, 0);
                }
                setError('');
            } catch (err) {
                console.error('Error fetching seller account:', err);
                setError(err.message || 'Failed to fetch seller account details.');
            } finally {
                setLoading(false);
            }
        };

        fetchSellerAccount();
    }, [user?.id]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBannerFile(file);
            setBannerPreviewUrl(URL.createObjectURL(file));
        } else {
            setBannerFile(null);
            setBannerPreviewUrl(formData.storefrontBannerUrl || '');
        }
    };

    const handleUploadBanner = async () => {
        if (!user?.id) {
            alert('User not authenticated.');
            return;
        }
        if (!bannerFile) {
            alert('Please select an image to upload.');
            return;
        }

        setUploadingBanner(true);
        try {
            const fileExt = bannerFile.name.split('.').pop();
            const filePath = `${user.id}/${Date.now()}.${fileExt}`;
            
            const { error: uploadError } = await supabase.storage
                .from('storefront-banners')
                .upload(filePath, bannerFile, {
                    cacheControl: '3600',
                    upsert: false,
                });

            if (uploadError) {
                throw uploadError;
            }

            const { data: { publicUrl } } = supabase.storage
                .from('storefront-banners')
                .getPublicUrl(filePath);

            await sellerService.updateSellerAccountForUser(user.id, { storefrontBannerUrl: publicUrl });

            setFormData(prev => ({ ...prev, storefrontBannerUrl: publicUrl }));
            setSellerAccount(prev => ({...prev, storefrontBannerUrl: publicUrl}));
            setBannerFile(null);

        } catch (uploadError) {
            console.error('Error uploading banner:', uploadError);
            alert('Error uploading banner: ' + uploadError.message);
            setBannerPreviewUrl(formData.storefrontBannerUrl || '');
        }
        finally {
            setUploadingBanner(false);
        }
    };

    const handleRemoveBanner = async () => {
        if (!user?.id) {
            alert('User not authenticated.');
            return;
        }
        if (!formData.storefrontBannerUrl) {
            alert('No banner to remove.');
            return;
        }

        setUploadingBanner(true);
        try {
            const filePath = formData.storefrontBannerUrl.split('storefront-banners/')[1];
            if (!filePath) {
                throw new Error('Could not parse file path from URL.');
            }

            const { error: deleteError } = await supabase.storage
                .from('storefront-banners')
                .remove([filePath]);

            if (deleteError) {
                throw deleteError;
            }

            await sellerService.updateSellerAccountForUser(user.id, { storefrontBannerUrl: '' });

            setFormData(prev => ({ ...prev, storefrontBannerUrl: '' }));
            setSellerAccount(prev => ({...prev, storefrontBannerUrl: ''}));
            setBannerPreviewUrl('');
            setBannerFile(null);
        } catch (removeError) {
            console.error('Error removing banner:', removeError);
            alert('Error removing banner: ' + removeError.message);
        } finally {
            setUploadingBanner(false);
        }
    };

    const handleAddCategory = () => {
        const trimmedCategory = newCategory.trim();
        if (trimmedCategory && !formData.storefrontCategories.includes(trimmedCategory)) {
            setFormData(prev => ({
                ...prev,
                storefrontCategories: [...prev.storefrontCategories, trimmedCategory],
            }));
            setNewCategory('');
        } else if (trimmedCategory) {
            alert('Category already exists or is empty.');
        }
    };

    const handleRemoveCategory = (categoryToRemove) => {
        setFormData(prev => ({
            ...prev,
            storefrontCategories: prev.storefrontCategories.filter(
                (category) => category !== categoryToRemove
            ),
            hiddenCategories: prev.hiddenCategories.filter(
                (category) => category !== categoryToRemove
            ),
        }));
    };

    const handleToggleCategoryVisibility = (category) => {
        setFormData(prev => {
            const isHidden = prev.hiddenCategories.includes(category);
            const updatedHidden = isHidden
                ? prev.hiddenCategories.filter(c => c !== category)
                : [...prev.hiddenCategories, category];
            return {
                ...prev,
                hiddenCategories: updatedHidden
            };
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (uploadingBanner) {
            alert('Please wait for the banner operation to complete.');
            return;
        }
        setIsSaving(true);
        setSaveSuccess(false);
        try {
            // Banner is already saved, so we only save the rest
            await sellerService.updateSellerAccountForUser(user.id, {
                storefrontCompanyMessage: formData.storefrontCompanyMessage,
                storefrontCategories: formData.storefrontCategories,
                storefrontActive: formData.storefrontActive,
                hidden_categories: formData.hiddenCategories,
            });
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000); // Revert after 2 seconds
        } catch (error) {
            console.error('Error saving storefront settings:', error);
            alert('Error saving storefront settings: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePreview = () => {
        if (!user?.id || !sellerAccount) {
            alert('User or seller account not available for preview.');
            return;
        }
        // Merge sellerAccount info with the current form state for a complete preview
        const previewData = {
            ...sellerAccount,
            ...formData,
        };
        sessionStorage.setItem('storefrontPreviewData', JSON.stringify(previewData));
        const previewUrl = `/store/${sellerAccount.businessSlug}?preview=true`;
        window.open(previewUrl, '_blank');
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    const isProTier = sellerAccount?.tier === 'platinum' || sellerAccount?.tier === 'enterprise';

    return (
        <form onSubmit={handleSave} className="space-y-8 p-4">
            {/* Top Row: Storefront Banner & Analytics side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Storefront Banner Card */}
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col justify-between">
                    <div>
                        <h3 className="text-lg font-medium leading-6 text-foreground">Storefront Banner</h3>
                        <p className="mt-1 text-sm text-muted-foreground mb-4">Upload a banner image for your store. Changes are saved immediately.</p>
                        <div className="mt-2 flex flex-col items-start space-y-4">
                            {(bannerPreviewUrl || formData.storefrontBannerUrl) ? (
                                <img src={bannerPreviewUrl || formData.storefrontBannerUrl} alt="Banner Preview" className="w-full h-36 object-cover rounded-md border border-border" />
                            ) : (
                                <div className="w-full h-36 border border-dashed border-border rounded-md flex flex-col items-center justify-center bg-muted/10 text-muted-foreground text-sm">
                                    <Icon name="Image" size={32} className="mb-2 text-muted-foreground/60 animate-pulse" />
                                    <span>No Banner Uploaded</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 flex flex-col space-y-2">
                        <div className="flex items-center space-x-2 w-full">
                            <Input
                                type="file"
                                id="banner-upload"
                                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            {bannerFile && (
                                <Button onClick={handleUploadBanner} disabled={uploadingBanner} className="text-xs py-1.5 px-3">
                                    {uploadingBanner ? 'Uploading...' : 'Upload & Save Banner'}
                                </Button>
                            )}
                            {formData.storefrontBannerUrl && !bannerFile && (
                                <Button variant="destructive" onClick={handleRemoveBanner} disabled={uploadingBanner} className="text-xs py-1.5 px-3">
                                    {uploadingBanner ? 'Removing...' : 'Remove Banner'}
                                </Button>
                            )}
                        </div>
                        {uploadingBanner && <p className="text-xs text-muted-foreground">Please wait...</p>}
                    </div>
                </div>

                {/* 7-Day Storefront Visits Graph Card */}
                <div className="bg-card border border-border rounded-lg p-6 shadow-sm flex flex-col justify-between">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-foreground">7-Day Storefront Traffic</h3>
                            <p className="mt-1 text-sm text-muted-foreground">Unique daily site visits over the past 7 days.</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <div 
                                className="flex items-center bg-muted px-3 py-1.5 rounded-full text-xs font-semibold text-foreground space-x-1 border border-border cursor-pointer relative group"
                                title={!isProTier ? "Upgrade to Creator Professional tier to unlock 90-day analytics history!" : undefined}
                            >
                                <Icon name="Activity" size={14} className="text-primary animate-pulse" />
                                <span>Live Analytics</span>
                                {!isProTier && (
                                    <span className="hidden group-hover:inline-block absolute bottom-full mb-2 right-0 w-64 p-3 bg-popover text-popover-foreground text-xs rounded-lg shadow-xl border border-border z-50 font-normal leading-normal text-left">
                                        📊 90-day historical trend analysis is available exclusively on the <strong>Creator Professional</strong> tier!
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {isProTier && (
                        <div className="flex items-center justify-between mb-4 bg-muted/30 p-2 rounded-lg border border-border/60">
                            <span className="text-xs font-semibold text-muted-foreground">Historical Controls:</span>
                            <div className="flex items-center space-x-2">
                                <button
                                    type="button"
                                    disabled={offsetDays >= 84 || loadingVisits}
                                    onClick={() => setOffsetDays(prev => Math.min(84, prev + 7))}
                                    className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-card hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-foreground flex items-center space-x-1 shadow-sm"
                                    title="Go back 7 days"
                                >
                                    <Icon name="ChevronLeft" size={14} />
                                    <span>Older</span>
                                </button>
                                <span className="text-[10px] font-bold text-primary px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20 whitespace-nowrap">
                                    {offsetDays === 0 ? 'Current Week' : `${offsetDays}d offset`}
                                </span>
                                <button
                                    type="button"
                                    disabled={offsetDays <= 0 || loadingVisits}
                                    onClick={() => setOffsetDays(prev => Math.max(0, prev - 7))}
                                    className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border bg-card hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-foreground flex items-center space-x-1 shadow-sm"
                                    title="Go forward 7 days"
                                >
                                    <span>Newer</span>
                                    <Icon name="ChevronRight" size={14} />
                                </button>
                                {offsetDays > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setOffsetDays(0)}
                                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
                                    >
                                        Current
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {loadingVisits ? (
                        <div className="h-44 flex items-center justify-center text-sm text-muted-foreground">
                            Loading traffic stats...
                        </div>
                    ) : visitsData.length > 0 ? (
                        <div className="h-44 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={visitsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--primary, #3b82f6)" stopOpacity={0.2}/>
                                            <stop offset="95%" stopColor="var(--primary, #3b82f6)" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(156, 163, 175, 0.15)" />
                                    <XAxis 
                                        dataKey="date" 
                                        stroke="rgba(156, 163, 175, 0.6)" 
                                        fontSize={11}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis 
                                        stroke="rgba(156, 163, 175, 0.6)" 
                                        fontSize={11}
                                        allowDecimals={false}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: 'var(--background, #ffffff)', 
                                            borderColor: 'var(--border, #e5e7eb)',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            color: 'var(--foreground, #000000)'
                                        }} 
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="visits" 
                                        stroke="var(--primary, #3b82f6)" 
                                        strokeWidth={2}
                                        fillOpacity={1} 
                                        fill="url(#colorVisits)" 
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="h-44 flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-lg bg-muted/20">
                            No traffic data recorded in this period.
                        </div>
                    )}
                </div>
            </div>

            <div>
                <h3 className="text-lg font-medium leading-6 text-foreground">Company Message</h3>
                <p className="mt-1 text-sm text-muted-foreground">A message to display on your storefront.</p>
                <Textarea
                    label="Company Message"
                    value={formData.storefrontCompanyMessage}
                    onChange={(e) => setFormData({ ...formData, storefrontCompanyMessage: e.target.value })}
                />
            </div>

            <div>
                <h3 className="text-lg font-medium leading-6 text-foreground">Product Categories</h3>
                <p className="mt-1 text-sm text-muted-foreground">Organize your products into categories. These will appear as tabs on your storefront.</p>
                <div className="mt-4 space-y-4">
                    {formData.storefrontCategories && formData.storefrontCategories.length > 0 && (
                        <div className="border border-border rounded-md p-4">
                            <h4 className="text-base font-semibold text-foreground mb-2">Current Categories:</h4>
                            <div className="flex flex-wrap gap-2">
                                {formData.storefrontCategories.map((category, index) => {
                                    const isHidden = formData.hiddenCategories?.includes(category);
                                    return (
                                        <div key={index} className={`flex items-center bg-muted rounded-full pl-3 pr-2 py-1.5 text-sm text-foreground transition-all duration-200 ${isHidden ? 'opacity-50 bg-red-50/50 border border-red-100 line-through' : 'border border-transparent'}`}>
                                            <span className="font-semibold">{category}</span>
                                            
                                            {/* Symmetrical Visibility Eye-Toggle */}
                                            <button
                                                type="button"
                                                onClick={() => handleToggleCategoryVisibility(category)}
                                                className="ml-2 p-1 rounded-full hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground"
                                                title={isHidden ? "Show category" : "Hide category"}
                                            >
                                                <Icon name={isHidden ? "EyeOff" : "Eye"} size={14} />
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCategory(category)}
                                                className="ml-1 p-1 rounded-full hover:bg-muted-foreground/20 text-muted-foreground hover:text-destructive"
                                                aria-label={`Remove ${category}`}
                                            >
                                                <Icon name="X" size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="flex space-x-2">
                        <Input
                            label="New Category Name"
                            placeholder="e.g., Furniture, Electronics"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            className="flex-grow"
                        />
                        <Button type="button" onClick={handleAddCategory}>
                            Add Category
                        </Button>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="text-lg font-medium leading-6 text-foreground">Storefront Activation</h3>
                <p className="mt-1 text-sm text-muted-foreground">Make your storefront public.</p>
                <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                    <div>
                        <h4 className="text-sm font-medium text-foreground">Storefront Active</h4>
                        <p className="text-xs text-muted-foreground">Toggle to activate or deactivate your storefront.</p>
                    </div>
                    <Switch
                        checked={formData.storefrontActive}
                        onCheckedChange={(newCheckedState) => {
                            setFormData({ ...formData, storefrontActive: newCheckedState });
                        }}
                    />
                </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-x-6">
                <Button type="button" variant="outline" onClick={handlePreview}>
                    Preview
                </Button>
                <Button type="submit" disabled={uploadingBanner || isSaving}>
                    {isSaving ? 'Saving...' : (saveSuccess ? 'Saved!' : 'Save All Other Changes')}
                </Button>
            </div>
        </form>
    );
};

export default MyStoreTab;
